import React, { createContext, useContext, useState, useEffect } from 'react'
import managementService from '../api/managementService'
import { useAuth } from '../context/AuthContext'

const ManagementContext = createContext()

export const ManagementProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [members, setMembers] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load management members from backend
  useEffect(() => {
    let isMounted = true;

    const fetchMembers = async () => {
      try {
        setLoading(true)
        setError(null)
        // Fetch all members (both active and inactive) for admin management
        const response = await managementService.getManagementMembers({ isActive: 'all' })
        
        if (isMounted && response.status === 'success' && response.data?.members) {
          setMembers(response.data.members)
        } else if (isMounted) {
          setMembers([])
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching management members:', err)
        }
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load management members')
          setMembers([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchMembers()

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [])

  // Load statistics (admin only)
  useEffect(() => {
    let isMounted = true;

    if (!isAuthenticated) {
      setStats(null)
      return
    }

    const fetchStats = async () => {
      try {
        const response = await managementService.getManagementStats()
        
        if (isMounted && response.status === 'success' && response.data) {
          setStats(response.data)
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching management stats:', err)
        }
        if (isMounted) {
          setStats(null)
        }
      }
    }

    fetchStats()

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated])

  // Add new management member
  const addMember = async (memberData) => {
    try {
      const formData = new FormData()

      // Append text fields
      Object.keys(memberData).forEach((key) => {
        if (key === 'image' && memberData[key] instanceof File) {
          formData.append('image', memberData[key])
        } else if (key === 'qualifications' && Array.isArray(memberData[key])) {
          formData.append('qualifications', memberData[key].join(','))
        } else if (key === 'socialLinks' && typeof memberData[key] === 'object') {
          formData.append('socialLinks', JSON.stringify(memberData[key]))
        } else if (memberData[key] !== undefined && memberData[key] !== null && memberData[key] !== '') {
          formData.append(key, memberData[key])
        }
      })

      const response = await managementService.createManagementMember(formData)

      if (response.status === 'success' && response.data?.member) {
        setMembers((prev) => [...prev, response.data.member])
        return response.data.member
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error adding management member:', err)
      }
      throw err
    }
  }

  // Update management member
  const updateMember = async (id, memberData) => {
    try {
      const formData = new FormData()

      // Fields that should not be sent to the backend (read-only or auto-managed)
      const excludedFields = ['_id', '__v', 'createdBy', 'createdAt', 'updatedAt']

      // Append text fields and handle image upload
      Object.keys(memberData).forEach((key) => {
        // Skip excluded fields
        if (excludedFields.includes(key)) {
          return
        }

        if (key === 'image' && memberData[key] instanceof File) {
          formData.append('image', memberData[key])
        } else if (key === 'qualifications' && Array.isArray(memberData[key])) {
          formData.append('qualifications', memberData[key].join(','))
        } else if (key === 'socialLinks' && typeof memberData[key] === 'object') {
          formData.append('socialLinks', JSON.stringify(memberData[key]))
        } else if (memberData[key] !== undefined && memberData[key] !== null && memberData[key] !== '') {
          formData.append(key, memberData[key])
        }
      })

      const response = await managementService.updateManagementMember(id, formData)

      if (response.status === 'success' && response.data?.member) {
        setMembers((prev) =>
          prev.map((member) => (member._id === id ? response.data.member : member))
        )
        return response.data.member
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error updating management member:', err)
      }
      throw err
    }
  }

  // Delete management member
  const deleteMember = async (id) => {
    try {
      await managementService.deleteManagementMember(id)
      setMembers((prev) => prev.filter((member) => member._id !== id))
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error deleting management member:', err)
      }
      throw err
    }
  }

  // Bulk delete management members
  const bulkDeleteMembers = async (ids) => {
    try {
      await managementService.bulkDeleteManagementMembers(ids)
      setMembers((prev) => prev.filter((member) => !ids.includes(member._id)))
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error bulk deleting management members:', err)
      }
      throw err
    }
  }

  // Get members by category
  const getMembersByCategory = (category) => {
    return (members || []).filter((member) => member.category === category && member.isActive)
  }

  // Get active members
  const getActiveMembers = () => {
    return (members || []).filter((member) => member.isActive)
  }

  // Get featured members
  const getFeaturedMembers = () => {
    return (members || []).filter((member) => member.featured && member.isActive)
  }

  const value = {
    members,
    stats,
    loading,
    error,
    addMember,
    updateMember,
    deleteMember,
    bulkDeleteMembers,
    getMembersByCategory,
    getActiveMembers,
    getFeaturedMembers,
  }

  return <ManagementContext.Provider value={value}>{children}</ManagementContext.Provider>
}

export const useManagement = () => {
  const context = useContext(ManagementContext)
  if (!context) {
    throw new Error('useManagement must be used within ManagementProvider')
  }
  return context
}

export default ManagementContext
